import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'
import { useGetChannelsByBuildingQuery } from '@/api';
import LastEventDisplay from './LastEventDisplay';
import EnteranceActionButtons from "./components/EnteranceActionButtons";
import { useSelector } from 'react-redux';
import { selectActiveBuildingId } from '@/features/building/buildingSlice';
import EnteranceEventsFeed from './components/EnteranceEvents/EnteranceEventsFeed';
import { toOrderedChannels } from './channelOrder';
import { useChannelOrder } from './useChannelOrder';
import { useChannelCardDrag } from './useChannelCardDrag';

const VIEW_LAST_EVENTS = 'lastEvents'
const VIEW_EVENTS_FEED = 'eventsFeed'

const Races = () => {
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const buildingId = Number(activeBuildingId)
  const [mobileView, setMobileView] = useState(VIEW_LAST_EVENTS)
  const {
    data,
    isSuccess,
    isError,
  } = useGetChannelsByBuildingQuery(buildingId)
  const channels = useMemo(() => toOrderedChannels(data), [data])
  const { ordered, commitOrder } = useChannelOrder(buildingId, channels)
  const { gridRef, dragHandleProps } = useChannelCardDrag(
    ordered.map((channel) => String(channel.id)),
    commitOrder,
  )
  const hasData = isSuccess && data?.length > 0;
  const hasNoData = isSuccess && !(data?.length !== 0);
  // Редактирование суммы вручную
  // const [manualAmount, setManualAmount] = useState(null)
  // const [amountEdited, setAmountEdited] = useState(false)

  const handleMobileViewChange = (_event, nextView) => {
    if (!nextView) return;
    setMobileView(nextView);
    window.scrollTo({ top: 0 });
  }

  return (
    <Grid 
      container
      spacing={2}
      size={12}
      sx={{
        pb: 2,
      }}
    >
      <RequestStatusAlert
        isError={isError}
        isListEmpty={hasNoData}
      />
      {
        hasData && (
          <MobileViewToggler
            value={mobileView}
            onChange={handleMobileViewChange}
          />
        )
      }
      {
        hasData && (
        <Grid
          container
          ref={gridRef}
          spacing={2}
          size={{
            xs: 12,
            md: 6,
            lg: 8,
            xl: 9,
          }}
          sx={{
            alignSelf: "flex-start",

            display: {
              xs: mobileView === VIEW_LAST_EVENTS ? 'flex' : 'none',
              md: 'flex',
            }
          }}>
          {
            ordered.map((channel) => (
              <Grid
                key={channel.id}
                data-channel-card=""
                data-channel-id={String(channel.id)}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 12,
                  lg: 6,
                  xl: 4,
                }}
                sx={{
                  '&[data-dragging="true"]': {
                    opacity: 0.85,
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                    borderRadius: 2,
                    zIndex: 2,
                  },
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <LastEventDisplay
                    channelName={channel.name}
                    channelId={channel.id}
                    dragHandleProps={dragHandleProps}
                  /> 
                  <EnteranceActionButtons
                    channelId={channel.id}
                  />
                </Paper>
              </Grid>
            ))
          }
        </Grid>
        )
      }
      {
        hasData && (
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
              xl: 3,
            }}
            sx={{
              display: {
                xs: mobileView === VIEW_EVENTS_FEED ? 'block' : 'none',
                md: 'block',
              },
            }}
          >
            <EnteranceEventsFeed
              key={activeBuildingId}
              channelsData={data}
            />
          </Grid>
        )
      }
    </Grid>
  );
}

const mobileViewOptions = [
  {
    value: VIEW_LAST_EVENTS,
    translationKey: 'races_view_last_events',
    icon: <CameraAltOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />,
  },
  {
    value: VIEW_EVENTS_FEED,
    translationKey: 'races_view_events_feed',
    icon: <ViewListOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />,
  },
]

const MobileViewToggler = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation()

  return (
    <Grid
      size={12}
      sx={(theme) => ({
        display: {
          xs: 'flex',
          md: 'none',
        },
        justifyContent: 'center',
        position: 'sticky',
        top: theme.constants.widgetOffsetTop + 6,
        zIndex: 2,
        borderColor: 'divider',
      })}
    >
      <ToggleButtonGroup
        color="primary"
        value={value}
        size="small"
        exclusive
        fullWidth
        onChange={onChange}
        sx={{
          backgroundColor: "background.blur",
        }}
      >
        {
          mobileViewOptions.map((option) => (
            <ToggleButton
              value={option.value}
              key={option.value}
              sx={(theme) => ({
                // backgroundColor: 'background.paper',
                textTransform: 'none',
                fontWeight: 400,
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                },
                '&.Mui-selected': {
                  color: theme.palette.text.primary,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: theme.palette.primary.main,
                },
              })}
            >
              {option.icon}
              {t(option.translationKey)}
            </ToggleButton>
          ))
        }
      </ToggleButtonGroup>
    </Grid>
  )
}

const RequestStatusAlert = ({
  isError,
  isListEmpty,
}) => {
  const { t } = useTranslation()

  return (
    isError ? (
      <Grid size={12}>
        <Alert severity="error">
          <AlertTitle>{t('error_occurred')}</AlertTitle>
          {t('error_occured_text')}
        </Alert>
      </Grid>
    ) 
    : isListEmpty ? (
      <Grid size={12}>
        <Alert severity="warning">
          <AlertTitle>{t('channels_list_empty')}</AlertTitle>
          {t('channels_list_empty_text')}
        </Alert>
      </Grid>
    ) : null
  )
}

export default Races
